import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, List, Skeleton } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { GroupAvatar } from '@/components'
import { GroupPrivateSetting, RequestedGroupFragment, useGetOnboardingRecomGroupsQuery } from '@/generated/graphql'
import { Follow, FollowAll } from '@/modules/follow'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage5: React.FC<OnboardingPageProps> = ({ onNext, onPrevious }) => {
  const { t } = useTranslation()

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const { data, loading } = useGetOnboardingRecomGroupsQuery({
    fetchPolicy: 'network-only',
    variables: {
      limit: 14,
    },
  })

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '5',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  const groups = data?.recommend.onboardingRecommendedGroups ?? []

  const groupIds = groups.map((g: RequestedGroupFragment) => g.groupId)

  const subHeader = t('onboarding.page5.subHeader')

  const getGroupList = (groups: RequestedGroupFragment[], style?: any) => {
    return groups.length > 0 ? (
      <div className={s.page5}>
        <List
          dataSource={groups}
          loading={loading}
          split={false}
          itemLayout='horizontal'
          size='small'
          style={style}
          className={s.list}
          renderItem={(g: RequestedGroupFragment) => (
            <List.Item
              key={g.groupId}
              style={{ paddingLeft: 0, paddingRight: 0 }}
              actions={[
                <Follow
                  display='icon'
                  key='follow'
                  followeeGroupId={g.groupId}
                  isFollowing={g.isFollowing}
                  isOnboarding={true}
                  isPrivate={g.privateSetting === GroupPrivateSetting.Closed}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<GroupAvatar groupCatId={g.category?.groupCategoryId} />}
                title={g.name}
                description={<div className={s.groupDes}>{g.description}</div>}
              />
            </List.Item>
          )}
        />
      </div>
    ) : null
  }

  const leftGroups = groups.slice(0, 7)
  const rightGroups = groups.slice(7, 14)
  const rightGroupExists = rightGroups.length > 0

  const left = getGroupList(leftGroups, rightGroupExists ? { marginRight: 12 } : undefined)
  const right = getGroupList(rightGroups, rightGroupExists ? { marginLeft: 12 } : undefined)

  const footer = (
    <>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '5',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '5',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </>
  )

  return (
    <OnboardingPageLayout
      header='DISCOVER GROUP'
      subHeader={subHeader}
      subHeaderAction={loading ? <Skeleton.Button /> : <FollowAll followeeIds={groupIds} target='group' />}
      left={left}
      right={right}
      footer={footer}
      percent={5}
    />
  )
}
